import { create } from 'zustand';
import { supabase } from '@/lib/supabase/client';

interface SubstageState {
  activities: any[];
  comments: any[];
  isLoading: boolean;
  error: string | null;
  fetchActivities: (substageId: string) => Promise<void>;
  fetchComments: (substageId: string) => Promise<void>;
  addComment: (substageId: string, content: string, mentions: string[]) => Promise<void>;
  deleteComment: (commentId: string) => Promise<void>;
}

export const useSubstageStore = create<SubstageState>((set, get) => ({
  activities: [],
  comments: [],
  isLoading: false,
  error: null,

  fetchActivities: async (substageId: string) => {
    try {
      set({ isLoading: true });
      const { data, error } = await supabase
        .from('substage_activities')
        .select(`
          *,
          user:user_id (
            id,
            full_name,
            email
          )
        `)
        .eq('substage_id', substageId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      set({ activities: data || [] });
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to fetch activities' });
    } finally {
      set({ isLoading: false });
    }
  },

  fetchComments: async (substageId: string) => {
    try {
      set({ isLoading: true });
      const { data, error } = await supabase
        .from('substage_comments')
        .select(`
          *,
          user:user_id (
            id,
            full_name,
            email
          )
        `)
        .eq('substage_id', substageId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      set({ comments: data || [] });
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to fetch comments' });
    } finally {
      set({ isLoading: false });
    }
  },

  addComment: async (substageId: string, content: string, mentions: string[]) => {
    try {
      set({ isLoading: true });
      
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');

      // Add comment
      const { data: comment, error } = await supabase
        .from('substage_comments')
        .insert([{
          substage_id: substageId,
          user_id: user.id,
          content,
          mentions
        }])
        .select(`
          *,
          user:user_id (
            id,
            full_name,
            email
          )
        `)
        .single();

      if (error) throw error;

      // Update comments list
      const { comments } = get();
      set({ comments: [...comments, comment] });

      // Also create an activity for the comment
      await supabase
        .from('substage_activities')
        .insert([{
          substage_id: substageId,
          user_id: user.id,
          action: 'commented',
          details: { content }
        }]);

      // Refresh activities
      await get().fetchActivities(substageId);

    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to add comment' });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  deleteComment: async (commentId: string) => {
    try {
      set({ isLoading: true });
      
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');

      // Delete the comment
      const { error } = await supabase
        .from('substage_comments')
        .delete()
        .eq('id', commentId)
        .eq('user_id', user.id); // Ensure only the comment owner can delete it

      if (error) throw error;

      // Update comments list
      const { comments } = get();
      set({ comments: comments.filter(comment => comment.id !== commentId) });

    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to delete comment' });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  }
}));

// Set up realtime subscription
supabase
  .channel('substage_changes')
  .on(
    'postgres_changes',
    { event: '*', schema: 'public', table: 'substage_activities' },
    (payload) => {
      const state = useSubstageStore.getState();
      const newPayload = payload.new as Record<string, unknown> | null;
      const substageId = newPayload?.substage_id as string | undefined;
      if (substageId) {
        state.fetchActivities(substageId);
      }
    }
  )
  .on(
    'postgres_changes',
    { event: '*', schema: 'public', table: 'substage_comments' },
    (payload) => {
      const state = useSubstageStore.getState();
      const newPayload = payload.new as Record<string, unknown> | null;
      const substageId = newPayload?.substage_id as string | undefined;
      if (substageId) {
        state.fetchComments(substageId);
      }
    }
  )
  .subscribe();